/**
 * PushAlarmService — Web Push subscription + server-side alarm scheduling.
 *
 * Primary mechanism: localStorage + WaitingScreen setTimeout (offline-first).
 * This service is the online enhancement: arms a server-side push that fires
 * even when the app is fully closed.
 *
 * Call order:
 *   PushAlarmService.register(scheduledForMs, wakeOffsetMs, retreatName)
 *     → subscribes (or reuses existing subscription)
 *     → POSTs to /schedule-alarm edge function
 *     → edge function inserts into pending_alarms, pg_cron fires push at fireAt
 *
 *   PushAlarmService.cancel(scheduledForMs)
 *     → POSTs to /cancel-alarm edge function (fire-and-forget)
 *
 *   PushAlarmService.flushPending()
 *     → called by SyncManager on 'online' event
 *     → reads rc_pending_alarm_registration from localStorage
 *     → retries the register() call if a pending registration exists
 */

import { createChildLogger } from "@retreat/shared";
import { getClient } from "@retreats/supabase-adapter";
import { STORAGE_KEYS, EDGE_FN } from "../constants";

const log = createChildLogger("PushAlarmService");

const PENDING_KEY = STORAGE_KEYS.PENDING_ALARM_REGISTRATION;

type PendingAlarm = {
  scheduledForMs: number;
  wakeOffsetMs: number;
  retreatName: string;
};

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

function getSupabaseUrl(): string {
  return (import.meta.env.VITE_SUPABASE_URL as string) ?? "";
}

function getVapidKey(): string {
  return (import.meta.env.VITE_VAPID_PUBLIC_KEY as string) ?? "";
}

function isStandalone(): boolean {
  if (!window.matchMedia) return false;
  return window.matchMedia("(display-mode: standalone)").matches;
}

function isPushSupported(): boolean {
  // iOS requires standalone mode for Web Push
  if (/iP(hone|ad|od)/.test(navigator.userAgent) && !isStandalone()) return false;
  return "PushManager" in window && "serviceWorker" in navigator;
}

async function getOrCreateSubscription(): Promise<PushSubscription | null> {
  const vapidKey = getVapidKey();
  if (!vapidKey) {
    log.warn("VITE_VAPID_PUBLIC_KEY not set — push registration skipped");
    return null;
  }

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;

  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey).buffer as ArrayBuffer,
  });
}

async function getAccessToken(): Promise<string | null> {
  try {
    const { data: { session } } = await getClient().auth.getSession();
    return session?.access_token ?? null;
  } catch {
    // getClient() throws when Supabase is not initialised (E2E mode, no env vars)
    return null;
  }
}

async function postToEdgeFunction(
  path: string,
  body: Record<string, unknown>,
): Promise<boolean> {
  const url = `${getSupabaseUrl()}/functions/v1/${path}`;
  const token = await getAccessToken();
  if (!token) {
    log.warn(`${path} skipped — no authenticated session`);
    return false;
  }
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    return res.ok;
  } catch (err) {
    log.warn(`${path} request failed`, { error: String(err) });
    return false;
  }
}

function savePending(pending: PendingAlarm): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch { /* quota exceeded */ }
}

function loadPending(): PendingAlarm | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as PendingAlarm) : null;
  } catch { return null; }
}

function clearPending(): void {
  try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
}

export const PushAlarmService = {
  /**
   * Register a wake-up alarm on the server.
   * If offline or push not supported, stores the request for later retry.
   *
   * Returns whether the server-side alarm is confirmed armed:
   *   true  — registered, or not applicable (unsupported platform, no
   *           permission, no VAPID key — nothing to confirm)
   *   false — attempted but unconfirmed (offline, non-ok response, or
   *           thrown error); queued for retry. Callers should warn the
   *           user that the local WaitingScreen countdown is their only
   *           guaranteed wake-up until this resolves.
   */
  async register(
    scheduledForMs: number,
    wakeOffsetMs: number,
    retreatName: string,
  ): Promise<boolean> {
    if (!isPushSupported()) return true;
    if (Notification.permission !== "granted") return true;

    if (!navigator.onLine) {
      savePending({ scheduledForMs, wakeOffsetMs, retreatName });
      log.info("Offline — alarm registration queued for retry");
      return false;
    }

    try {
      const sub = await getOrCreateSubscription();
      if (!sub) return true;

      const ok = await postToEdgeFunction(EDGE_FN.SCHEDULE_ALARM, {
        subscription: sub.toJSON(),
        scheduledForMs,
        wakeOffsetMs,
        retreatName,
      });

      if (ok) {
        clearPending();
        log.info("Alarm registered", { scheduledForMs, wakeOffsetMs });
        return true;
      }
      savePending({ scheduledForMs, wakeOffsetMs, retreatName });
      log.warn(`${EDGE_FN.SCHEDULE_ALARM} returned non-ok — queued for retry`);
      return false;
    } catch (err) {
      savePending({ scheduledForMs, wakeOffsetMs, retreatName });
      log.warn("register threw — queued for retry", { error: String(err) });
      return false;
    }
  },

  /**
   * Cancel a previously registered alarm. Fire-and-forget: if this fails
   * (e.g. offline), the server's pg_cron job will skip the row once fire_at
   * passes — no stale notification fires for cancelled retreats.
   */
  async cancel(scheduledForMs: number): Promise<void> {
    clearPending();
    if (!navigator.onLine) return;
    await postToEdgeFunction(EDGE_FN.CANCEL_ALARM, { scheduledForMs });
  },

  /**
   * Re-arm the alarm for a retreat with a new wake offset: cancels the
   * previous registration, then registers the new one. Single source of
   * truth for "change wake offset" so callers don't duplicate the
   * cancel-then-register glue. Returns register()'s confirmation result.
   */
  async reschedule(
    scheduledForMs: number,
    wakeOffsetMs: number,
    retreatName: string,
  ): Promise<boolean> {
    await PushAlarmService.cancel(scheduledForMs);
    return PushAlarmService.register(scheduledForMs, wakeOffsetMs, retreatName);
  },

  /**
   * Called by SyncManager on 'online' event. Retries any pending registration
   * that failed while the device was offline.
   *
   * Returns null when there was nothing queued (no-op), otherwise the retry's
   * confirmation result — same semantics as register().
   */
  async flushPending(): Promise<boolean | null> {
    const pending = loadPending();
    if (!pending) return null;
    log.info("Flushing pending alarm registration");
    return PushAlarmService.register(
      pending.scheduledForMs,
      pending.wakeOffsetMs,
      pending.retreatName,
    );
  },

  /** True if the current platform can receive Web Push notifications. */
  isSupported(): boolean {
    return isPushSupported();
  },

  /** True if installed to home screen (required for iOS Web Push). */
  isStandalone(): boolean {
    return isStandalone();
  },
};
